import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import {
  Cloud, Monitor, Check, X, ArrowRight,
  Wifi, Globe, MessageCircle, CreditCard, HardDrive,
  RefreshCw, Headphones, Database, Shield, Zap
} from "lucide-react";
import VersionQuiz from "./VersionQuiz";
import { useLanguage } from "@/context/LanguageContext";

const featureKeys = [
  { labelKey: "feat_access", cloudKey: "feat_access_cloud", localKey: "feat_access_local", cloudValue: true, localValue: true, icon: Wifi },
  { labelKey: "feat_whatsapp", cloudKey: "feat_whatsapp_cloud", localKey: "feat_whatsapp_local", cloudValue: true, localValue: false, icon: MessageCircle },
  { labelKey: "feat_domain", cloudKey: "feat_domain_cloud", localKey: "feat_domain_local", cloudValue: true, localValue: false, icon: Globe },
  { labelKey: "feat_tracking", cloudKey: "feat_tracking_cloud", localKey: "feat_tracking_local", cloudValue: true, localValue: false, icon: Shield },
  { labelKey: "feat_payment", cloudKey: "feat_payment_cloud", localKey: "feat_payment_local", cloudValue: true, localValue: true, icon: CreditCard },
  { labelKey: "feat_recurring", cloudKey: "feat_recurring_cloud", localKey: "feat_recurring_local", cloudValue: true, localValue: false, icon: RefreshCw },
  { labelKey: "feat_updates", cloudKey: "feat_updates_cloud", localKey: "feat_updates_local", cloudValue: true, localValue: true, icon: Zap },
  { labelKey: "feat_storage", cloudKey: "feat_storage_cloud", localKey: "feat_storage_local", cloudValue: true, localValue: true, icon: Database },
  { labelKey: "feat_support", cloudKey: "feat_support_cloud", localKey: "feat_support_local", cloudValue: true, localValue: true, icon: Headphones },
  { labelKey: "feat_install", cloudKey: "feat_install_cloud", localKey: "feat_install_local", cloudValue: true, localValue: true, icon: HardDrive },
];

const VersionComparisonSection = () => {
  const [activeTab, setActiveTab] = useState("both");
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const { t } = useLanguage();

  const features = featureKeys.map((f) => ({
    label: t(f.labelKey),
    cloud: t(f.cloudKey),
    local: t(f.localKey),
    cloudValue: f.cloudValue,
    localValue: f.localValue,
    icon: f.icon,
  }));

  return (
    <section
      ref={sectionRef}
      id="versiones"
      data-testid="version-comparison-section"
      className="relative py-24 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#02040a] via-[#0a0f1e] to-[#02040a]" />

      {/* Glow effects */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[300px] bg-[#06b6d4]/5 blur-[120px] rounded-full" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-[#10b981]/5 blur-[120px] rounded-full" />

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.3em] text-[#06b6d4] border border-[#06b6d4]/20 bg-[#06b6d4]/5 mb-6">
            {t("comparison_badge")}
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t("comparison_title_1")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10b981] to-[#06b6d4]">
              {t("comparison_title_2")}
            </span>
          </h2>

          <p className="text-[#94a3b8] text-base sm:text-lg max-w-2xl mx-auto">
            {t("comparison_subtitle")}
          </p>
        </motion.div>

        {/* Toggle tabs */}
        <motion.div
          className="flex justify-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          <div className="inline-flex rounded-xl bg-[#0a0f1e] border border-white/10 p-1" data-testid="version-toggle">
            {[
              { id: "both", label: t("comparison_compare") },
              { id: "cloud", label: "Cloud", icon: Cloud, color: "#06b6d4" },
              { id: "local", label: "Local", icon: Monitor, color: "#10b981" },
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`version-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? "text-white"
                    : "text-[#64748b] hover:text-[#94a3b8]"
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeVersionTab"
                    className="absolute inset-0 rounded-lg"
                    style={{
                      backgroundColor: tab.color ? `${tab.color}20` : "#3b82f620",
                      border: `1px solid ${tab.color || "#3b82f6"}40`,
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                {tab.icon && <tab.icon size={14} className="relative z-10" />}
                <span className="relative z-10">{tab.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Comparison Table */}
        <AnimatePresence mode="wait">
          {activeTab === "both" ? (
            <motion.div
              key="comparison"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              data-testid="comparison-table"
            >
              {/* Column headers - hidden on mobile */}
              <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr] gap-0 mb-1 px-4">
                <div />
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-t-xl bg-[#06b6d4]/10 border border-[#06b6d4]/20 border-b-0">
                    <Cloud size={16} className="text-[#06b6d4]" />
                    <span className="text-[#06b6d4] font-bold text-sm">{t("version_cloud")}</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-t-xl bg-[#10b981]/10 border border-[#10b981]/20 border-b-0">
                    <Monitor size={16} className="text-[#10b981]" />
                    <span className="text-[#10b981] font-bold text-sm">{t("version_local")}</span>
                  </div>
                </div>
              </div>

              {/* Desktop table rows */}
              <div className="hidden sm:block rounded-2xl border border-white/5 bg-[#080e1c] overflow-hidden">
                {features.map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.3 + i * 0.05 }}
                      className={`grid grid-cols-[1fr_1fr_1fr] gap-0 items-center ${
                        i !== features.length - 1 ? "border-b border-white/5" : ""
                      } ${i % 2 === 0 ? "bg-white/[0.02]" : ""}`}
                    >
                      {/* Feature name */}
                      <div className="flex items-center gap-3 px-5 py-4">
                        <Icon size={16} className="text-[#64748b] shrink-0" />
                        <span className="text-white text-sm font-medium">{feature.label}</span>
                      </div>

                      {/* Cloud value */}
                      <div className="text-center px-4 py-4 border-x border-white/5">
                        <div className="flex items-center justify-center gap-2">
                          {feature.cloudValue ? (
                            <Check size={14} className="text-[#06b6d4] shrink-0" />
                          ) : (
                            <X size={14} className="text-[#475569] shrink-0" />
                          )}
                          <span className="text-[#94a3b8] text-xs">{feature.cloud}</span>
                        </div>
                      </div>

                      {/* Local value */}
                      <div className="text-center px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {feature.localValue ? (
                            <Check size={14} className="text-[#10b981] shrink-0" />
                          ) : (
                            <X size={14} className="text-[#475569] shrink-0" />
                          )}
                          <span className="text-[#94a3b8] text-xs">{feature.local}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Mobile card layout */}
              <div className="sm:hidden space-y-3">
                {features.map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: 0.2 + i * 0.04 }}
                      className="rounded-xl border border-white/5 bg-[#080e1c] p-4"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Icon size={14} className="text-[#64748b]" />
                        <span className="text-white text-sm font-medium">{feature.label}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-lg bg-[#06b6d4]/5 border border-[#06b6d4]/10 p-2.5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Cloud size={10} className="text-[#06b6d4]" />
                            <span className="text-[#06b6d4] text-[10px] font-semibold">Cloud</span>
                          </div>
                          <div className="flex items-start gap-1.5">
                            {feature.cloudValue ? (
                              <Check size={11} className="text-[#06b6d4] mt-0.5 shrink-0" />
                            ) : (
                              <X size={11} className="text-[#475569] mt-0.5 shrink-0" />
                            )}
                            <span className="text-[#94a3b8] text-[10px] leading-tight">{feature.cloud}</span>
                          </div>
                        </div>
                        <div className="rounded-lg bg-[#10b981]/5 border border-[#10b981]/10 p-2.5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Monitor size={10} className="text-[#10b981]" />
                            <span className="text-[#10b981] text-[10px] font-semibold">Local</span>
                          </div>
                          <div className="flex items-start gap-1.5">
                            {feature.localValue ? (
                              <Check size={11} className="text-[#10b981] mt-0.5 shrink-0" />
                            ) : (
                              <X size={11} className="text-[#475569] mt-0.5 shrink-0" />
                            )}
                            <span className="text-[#94a3b8] text-[10px] leading-tight">{feature.local}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              data-testid={`version-detail-${activeTab}`}
            >
              <div className="rounded-2xl border border-white/5 bg-[#080e1c] overflow-hidden p-4 sm:p-8">
                <div className="flex items-center gap-4 mb-8">
                  {activeTab === "cloud" ? (
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-[#06b6d4]/10 border border-[#06b6d4]/30">
                      <Cloud size={28} className="text-[#06b6d4]" />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-[#10b981]/10 border border-[#10b981]/30">
                      <Monitor size={28} className="text-[#10b981]" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {activeTab === "cloud" ? t("version_cloud") : t("version_local")}
                    </h3>
                    <p className="text-sm text-[#64748b]">
                      {activeTab === "cloud"
                        ? t("comparison_cloud_sub")
                        : t("comparison_local_sub")}
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {features.map((feature, i) => {
                    const Icon = feature.icon;
                    const isActive = activeTab === "cloud" ? feature.cloudValue : feature.localValue;
                    const desc = activeTab === "cloud" ? feature.cloud : feature.local;
                    const color = activeTab === "cloud" ? "#06b6d4" : "#10b981";

                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                          isActive
                            ? "border-white/10 bg-white/[0.03]"
                            : "border-white/5 bg-white/[0.01] opacity-50"
                        }`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                          style={{
                            backgroundColor: isActive ? `${color}15` : "#1e293b",
                          }}
                        >
                          {isActive ? (
                            <Check size={14} style={{ color }} />
                          ) : (
                            <X size={14} className="text-[#475569]" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Icon size={13} className="text-[#64748b]" />
                            <span className="text-white text-sm font-medium">{feature.label}</span>
                          </div>
                          <p className="text-[#64748b] text-xs mt-1">{desc}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <p className="text-[#94a3b8] text-sm mb-5">
            {t("comparison_cta_text")}
          </p>
          <motion.button
            data-testid="version-cta-contact"
            onClick={() =>
              document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#06b6d4] to-[#10b981] text-white font-bold text-sm hover:opacity-90 transition-opacity"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {t("comparison_cta_btn")}
            <ArrowRight size={16} />
          </motion.button>
          <p className="text-[#3b82f6]/30 text-xs font-semibold tracking-widest uppercase mt-4">
            Global Metafora APP
          </p>
        </motion.div>

        {/* Quiz */}
        <VersionQuiz />
      </div>
    </section>
  );
};

export default VersionComparisonSection;
